import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { generateCSSVariables } from '@theonebrand/brand-theme';
import type { BrandTheme } from '@theonebrand/brand-theme';

interface BrandingContextValue {
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  tabName: string;
  isLoading: boolean;
}

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.theonestack.com/api/security';
const DEFAULT_PRIMARY_COLOR = '#ef4444'; // Red (Security product color)
const DEFAULT_TAB_NAME = 'The One Security';

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [tabName, setTabName] = useState(DEFAULT_TAB_NAME);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch brand theme from API
  useEffect(() => {
    const controller = new AbortController();

    async function loadBrand() {
      try {
        const res = await fetch(`${API_BASE}/brand/theme`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!res.ok) {
          setIsLoading(false);
          return;
        }
        const data = await res.json() as BrandTheme & {
          name?: string;
          logo?: { primary_url?: string; favicon_url?: string };
        };

        if (data.colors?.primary) setPrimaryColor(data.colors.primary);
        if (data.logo?.primary_url) setLogoUrl(data.logo.primary_url);
        if (data.logo?.favicon_url) setFaviconUrl(data.logo.favicon_url);
        if (data.name) setTabName(data.name);
      } catch {
        // Brand API unavailable — use default red
      } finally {
        setIsLoading(false);
      }
    }

    loadBrand();
    return () => controller.abort();
  }, []);

  // Apply CSS variables when color changes
  useEffect(() => {
    document.documentElement.style.setProperty('--brand-primary', primaryColor);

    const hex = primaryColor.replace('#', '');
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      document.documentElement.style.setProperty('--brand-primary-rgb', `${r}, ${g}, ${b}`);
    }

    const brandTheme = { colors: { primary: primaryColor || DEFAULT_PRIMARY_COLOR } };
    const cssText = generateCSSVariables(brandTheme as Parameters<typeof generateCSSVariables>[0]);
    let styleEl = document.getElementById('brand-theme-vars');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'brand-theme-vars';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = cssText;
  }, [primaryColor]);

  useEffect(() => {
    if (!faviconUrl) return;
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }, [faviconUrl]);

  useEffect(() => {
    document.title = tabName;
  }, [tabName]);

  const value: BrandingContextValue = useMemo(
    () => ({ logoUrl, faviconUrl, primaryColor, tabName, isLoading }),
    [logoUrl, faviconUrl, primaryColor, tabName, isLoading],
  );

  return (
    <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
  );
}

export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (ctx === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return ctx;
}
